import type { CSSProperties, ReactNode } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface OnboardingFrameProps {
  currentStep: 1 | 2 | 3;
  totalSteps?: number;
  badge: string;
  title: string;
  subtitle: string;
  children: ReactNode;
}

const STEP_KEYS = ['step1', 'step2', 'step3'] as const;

export default function OnboardingFrame({
  currentStep,
  totalSteps = 3,
  badge,
  title,
  subtitle,
  children,
}: OnboardingFrameProps) {
  const { t } = useTranslation('owner');

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bl-bg-page, #f4f5f7)',
        padding: '16px 16px 96px',
      }}
    >
      {/* ─── Hero Card ───────────────────────────────────────────────── */}
      <div
        className="bl-animate-in"
        style={{
          position: 'relative',
          borderRadius: 18,
          background:
            'linear-gradient(135deg, #1677ff 0%, #3390ec 55%, #5da9f5 100%)',
          padding: '18px 18px 20px',
          marginBottom: 14,
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(22,119,255,0.28)',
        }}
      >
        {/* Decorative orbs */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -40,
            right: -30,
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.12)',
            filter: 'blur(6px)',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            bottom: -50,
            left: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            filter: 'blur(8px)',
          }}
        />

        <div style={{ position: 'relative' }}>
          {/* Eyebrow badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.8,
              padding: '4px 10px',
              borderRadius: 999,
              marginBottom: 12,
              backdropFilter: 'blur(8px)',
            }}
          >
            {badge}
          </div>

          {/* Title */}
          <Text
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: -0.5,
              display: 'block',
              lineHeight: 1.2,
              marginBottom: 6,
            }}
          >
            {title}
          </Text>

          {/* Subtitle */}
          <Text
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.82)',
              display: 'block',
              lineHeight: 1.45,
            }}
          >
            {subtitle}
          </Text>

          {/* Progress dots */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 16,
            }}
          >
            <div style={{ display: 'flex', gap: 5, flex: 1 }}>
              {STEP_KEYS.slice(0, totalSteps).map((_, idx) => {
                const stepNum = idx + 1;
                const isDone = stepNum < currentStep;
                const isCurrent = stepNum === currentStep;
                return (
                  <div
                    key={idx}
                    style={{
                      flex: isCurrent ? 2.4 : 1,
                      height: 4,
                      borderRadius: 999,
                      background: isDone
                        ? 'rgba(255,255,255,0.95)'
                        : isCurrent
                          ? 'rgba(255,255,255,0.95)'
                          : 'rgba(255,255,255,0.32)',
                      transition: 'flex 0.3s ease, background 0.3s ease',
                    }}
                  />
                );
              })}
            </div>
            <Text
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#fff',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {t('onboarding.progress.stepLabel', {
                current: currentStep,
                total: totalSteps,
              })}
            </Text>
          </div>
        </div>
      </div>

      {/* ─── Step Content ────────────────────────────────────────────── */}
      <div className="bl-animate-in bl-animate-in-delay-1">{children}</div>
    </div>
  );
}

// ─── Reusable card primitive ──────────────────────────────────────────

export function OnboardingCard({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 2px 8px rgba(16,24,40,0.08))',
        padding: '16px 16px',
        marginBottom: 12,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Section header inside cards ──────────────────────────────────────

export function CardSectionHeader({
  icon,
  iconColor = '#1677ff',
  title,
  subtitle,
}: {
  icon?: ReactNode;
  iconColor?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 14,
      }}
    >
      {icon && (
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: iconColor + '18',
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 17,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--bl-text-strong, #0f172a)',
            display: 'block',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Text>
        {subtitle && (
          <Text
            style={{
              fontSize: 12,
              color: 'var(--bl-text-muted, #6b7280)',
              display: 'block',
              marginTop: 2,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </Text>
        )}
      </div>
    </div>
  );
}
